// src/App.tsx
import React, { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import AuthProvider from './contexts/AuthProvider';
import AppRouter from './routes/Router';
import AuthGate from './components/AuthGate';
import LoginPromptBanner from './components/LoginPromptBanner';
import { NAV_HEIGHT } from './components/BottomNav';
import ChushoAppHeader from './components/apps/chusho-app/ChushoAppHeader';
import ChushoAppBottomNav from './components/apps/chusho-app/ChushoAppBottomNav';
import GhouseishosiHeader from './components/apps/ghouseishosi/GhouseishosiHeader';
import GhouseishosiBottomNav from './components/apps/ghouseishosi/GhouseishosiBottomNav';
import UpdateNotification from './components/UpdateNotification';
import LevelUpWatcher from './components/three/LevelUpWatcher';
import OfflineBanner from './components/OfflineBanner';
import CustomInstallButton from './components/CustomInstallButton';
import UIToggleButton from './components/UIToggleButton';
import { SubjectsNavigationProvider } from './contexts/SubjectsNavigationContext';
import { ScrollPositionProvider } from './contexts/ScrollPositionContext';
import { UIVisibilityProvider, useUIVisibility } from './contexts/UIVisibilityContext';
import { AppIdProvider, useAppId } from './contexts/AppIdContext';
import { AliveScope } from 'react-activation';
import { SnackbarProvider } from 'notistack';
import { useAppVersionWatch } from './hooks/useAppVersionWatch';
import { usePageTracking } from './hooks/usePageTracking';
import { useUserTracking } from './hooks/useUserTracking';
import { captureFirstTouch } from '@/utils/funnel';
import { initGA } from './utils/analytics';

const AppContent: React.FC = () => {
  useAppVersionWatch();
  usePageTracking();
  useUserTracking();

  // 流入元（utm / リファラ / 着地パス）を初回1度だけ記録する
  React.useEffect(() => { captureFirstTouch(); }, []);
  const { isHeaderVisible, isFooterVisible } = useUIVisibility();
  const { appId, isValidApp } = useAppId();

  // appIdに応じたヘッダー/フッターコンポーネントを選択
  const Header = appId === 'chusho-app' ? ChushoAppHeader :
                  appId === 'ghouseishosi' ? GhouseishosiHeader :
                  null;

  const BottomNav = appId === 'chusho-app' ? ChushoAppBottomNav :
                    appId === 'ghouseishosi' ? GhouseishosiBottomNav :
                    null;

  return (
    <>
      <OfflineBanner />
      {isHeaderVisible && Header && <Header />}
      {/* ログイン促進バナー（ヘッダーの直後、固定表示） */}
      {isValidApp && <LoginPromptBanner />}
      <div
        style={{
          minHeight: '100vh',
          // ヘッダーはセーフエリア分だけ高くなるため、本文の余白も inset を加算する
          paddingTop: isHeaderVisible && Header ? 'calc(64px + env(safe-area-inset-top))' : 0,
          paddingBottom: isFooterVisible && BottomNav ? `calc(${NAV_HEIGHT}px + env(safe-area-inset-bottom))` : 0,
          transition: 'padding 0.3s ease'
        }}
      >
        {/* 認証とチュートリアルのゲート */}
        <AuthGate>
          <AppRouter />
        </AuthGate>
      </div>
      {isFooterVisible && BottomNav && <BottomNav />}
      {appId && <UIToggleButton />}
      <LevelUpWatcher />
      <UpdateNotification />
      {/* <CustomInstallButton /> */}
    </>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    // GA4を初期化
    initGA();
  }, []);

  // PayPal は 2026-09-29 に**経路ごと削除**した（サーバーの /paypal/* 4本、
  // 未描画だった MembershipOptions / PremiumUpgradePage / SubscriptionCancelButton、
  // services/subscriptionService も同時に削除）。課金は Stripe 一本。

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <SnackbarProvider maxSnack={3}>
          <Router>
            <AppIdProvider>
              <UIVisibilityProvider>
                <SubjectsNavigationProvider>
                  <ScrollPositionProvider>
                    <AliveScope>
                      <AppContent />
                    </AliveScope>
                  </ScrollPositionProvider>
                </SubjectsNavigationProvider>
              </UIVisibilityProvider>
            </AppIdProvider>
          </Router>
        </SnackbarProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
